import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Home } from 'lucide-react';
import { AbstractionEngine } from '../components/AbstractionEngine';
import { BoogieWoogieSonifier } from '../components/BoogieWoogieSonifier';
import { DigitalSpirit } from '../components/DigitalSpirit';
import { ShareCard } from '../components/ShareCard';
import { EvolutionSlider } from '../components/EvolutionSlider';
import { DiagonalPolice } from '../components/DiagonalPolice';
import { RhythmTapper } from '../components/RhythmTapper';
import { MondrianButton } from '../components/ui/MondrianButton';
type Level = 1 | 2 | 3;
type Tool = 'rhythm' | 'sonifier' | 'slider' | 'police' | 'engine' | 'spirit' | 'share';
interface WorkbenchPageProps {
  onGoHome: () => void;
}
export function WorkbenchPage({
  onGoHome
}: WorkbenchPageProps) {
  const [currentLevel, setCurrentLevel] = useState<Level>(1);
  const [currentTool, setCurrentTool] = useState<Tool>('rhythm');
  const levels = [{
    id: 1,
    title: '感知 (Perception)',
    desc: '用音乐和节奏感知抽象艺术的生命力',
    color: 'bg-mondrian-yellow',
    tools: [{
      id: 'rhythm',
      name: '爵士律动台'
    }, {
      id: 'sonifier',
      name: '布吉伍吉奏鸣器'
    }]
  }, {
    id: 2,
    title: '理解 (Comprehension)',
    desc: '理解蒙德里安的减法哲学与严苛规则',
    color: 'bg-mondrian-red',
    tools: [{
      id: 'slider',
      name: '抽象时光机'
    }, {
      id: 'police',
      name: '斜线警探'
    }]
  }, {
    id: 3,
    title: '创造 (Creation)',
    desc: '创作你自己的新造型主义作品',
    color: 'bg-mondrian-blue',
    tools: [{
      id: 'engine',
      name: '抽象进化引擎'
    }, {
      id: 'spirit',
      name: '数字灵魂'
    }, {
      id: 'share',
      name: '分享卡片'
    }]
  }];
  const handleLevelChange = (level: Level) => {
    setCurrentLevel(level);
    // Set default tool for that level
    const defaultTool = levels.find(l => l.id === level)?.tools[0].id as Tool;
    setCurrentTool(defaultTool);
  };
  return <div className="min-h-screen bg-mondrian-white flex flex-col" data-id="element-269">
      {/* Level Navigation Header */}
      <header className="border-b-8 border-mondrian-black bg-white sticky top-0 z-50 shadow-md" data-id="element-270">
        <div className="max-w-7xl mx-auto" data-id="element-271">
          <div className="flex flex-col md:flex-row" data-id="element-272">
            {/* Logo Area - Clickable to go home */}
            <button onClick={onGoHome} className="p-4 border-b-4 md:border-b-0 md:border-r-4 border-mondrian-black bg-mondrian-black text-white min-w-[200px] flex items-center justify-center gap-2 hover:bg-mondrian-red transition-colors group cursor-pointer" title="返回首页" data-id="element-273">
              <Home className="w-5 h-5 group-hover:scale-110 transition-transform" data-id="element-274" />
              <h1 className="font-black text-xl uppercase tracking-wider" data-id="element-275">
                Mondrian Lab
              </h1>
            </button>

            {/* Level Tabs */}
            <div className="flex-1 flex overflow-x-auto" data-id="element-276">
              {levels.map(level => <div key={level.id} onClick={() => handleLevelChange(level.id as Level)} className={`
                    flex-1 min-w-[150px] p-4 cursor-pointer transition-all duration-300 border-r-4 border-mondrian-black last:border-r-0 relative overflow-hidden group
                    ${currentLevel === level.id ? level.color : 'bg-white hover:bg-gray-50'}
                  `} data-id="element-277">
                  <div className="relative z-10" data-id="element-278">
                    <div className="text-xs font-bold opacity-70 uppercase mb-1" data-id="element-279">
                      Level {level.id}
                    </div>
                    <div className={`font-black text-lg ${currentLevel === level.id && level.id === 3 ? 'text-white' : 'text-black'}`} data-id="element-280">
                      {level.title}
                    </div>
                  </div>

                  {/* Active Indicator */}
                  {currentLevel === level.id && <motion.div layoutId="active-level" className="absolute bottom-0 left-0 right-0 h-2 bg-black" data-id="element-281" />}
                </div>)}
            </div>
          </div>
        </div>
      </header>

      {/* Level Description & Sub-nav */}
      <div className="bg-white border-b-4 border-mondrian-black py-6 px-4" data-id="element-282">
        <div className="max-w-5xl mx-auto" data-id="element-283">
          <motion.div key={currentLevel} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-6 text-center" data-id="element-284">
            <h2 className="text-2xl font-bold mb-2" data-id="element-285">
              {levels[currentLevel - 1].title}
            </h2>
            <p className="text-gray-600" data-id="element-286">{levels[currentLevel - 1].desc}</p>
          </motion.div>

          {/* Tool Tabs */}
          <div className="flex justify-center gap-4 flex-wrap" data-id="element-287">
            {levels[currentLevel - 1].tools.map(tool => <MondrianButton key={tool.id} variant={currentTool === tool.id ? 'black' : 'white'} onClick={() => setCurrentTool(tool.id as Tool)} className="min-w-[140px]" data-id="element-288">
                {tool.name}
              </MondrianButton>)}
          </div>
        </div>
      </div>

      {/* Main Workspace */}
      <main className="flex-1 max-w-6xl mx-auto w-full p-4 md:p-8" data-id="element-289">
        <AnimatePresence mode="wait" data-id="element-290">
          <motion.div key={currentTool} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.3
        }} className="bg-white border-4 border-mondrian-black p-4 md:p-8 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]" data-id="element-291">
            {/* Level 1 Tools */}
            {currentTool === 'rhythm' && <RhythmTapper data-id="element-292" />}
            {currentTool === 'sonifier' && <BoogieWoogieSonifier data-id="element-293" />}

            {/* Level 2 Tools */}
            {currentTool === 'slider' && <EvolutionSlider data-id="element-294" />}
            {currentTool === 'police' && <DiagonalPolice data-id="element-295" />}

            {/* Level 3 Tools */}
            {currentTool === 'engine' && <AbstractionEngine onComplete={() => {}} data-id="element-296" />}
            {currentTool === 'spirit' && <DigitalSpirit data-id="element-297" />}
            {currentTool === 'share' && <ShareCard data-id="element-298" />}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>;
}